import {
  LIST_ALERTS_MANAGEMENT,
  OrderByInput,
  useAlertsManagementListFilter,
  useAlertsManagementListOrder,
  useCurrentWorkspace,
  useSetAlertsManagementListOrder,
  useUpdateAlertsStatus,
} from '@gripp/shared-logic';
import { ColumnDef } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteDataGrid } from '../infiniteDataGrid';
import { AlertsManagementTableData, useColumns } from './useColumns';

export type AlertsManagementListProps = {
  maxHeight?: number;
  paddingBottom?: number;
  preserveScrollKey?: string;
  editAlertMenu: (props: {
    alertId: string;
    label: string;
  }) => React.ReactElement;
  onRowClick: (alert: AlertsManagementTableData) => void;
  alertsStatusButton?: (props: {
    alertIds: string[];
    isEnableAction: boolean;
    onStatusChange: () => void;
  }) => React.ReactElement;
};

export const AlertsManagementList = ({
  maxHeight,
  paddingBottom,
  preserveScrollKey,
  editAlertMenu,
  onRowClick,
  alertsStatusButton,
}: AlertsManagementListProps) => {
  const { t } = useTranslation();

  const EditAlertMenu = editAlertMenu;

  const { data } = useAlertsManagementListFilter();
  const { data: orderData } = useAlertsManagementListOrder();
  const { mutateAsync: setOrder } = useSetAlertsManagementListOrder();
  const workspace = useCurrentWorkspace(true);

  const setOrderData = useCallback(
    (order: OrderByInput) => {
      setOrder(order);
    },
    [setOrder]
  );

  const [filter, setFilter] = useState<any>(undefined);
  const [columns, setColumns] = useState<string[]>([]);
  const [alertsSelected, setAlertsSelected] = useState<AlertsManagementTableData[]>([]);

  const showAlertMenu = useMemo(() => {
    return alertsSelected?.length === 1 ? true : false;
  }, [alertsSelected]);

  const showStatusButton = useMemo(() => {
    return alertsSelected && alertsSelected.length > 0;
  }, [alertsSelected]);

  const isEnableAction = useMemo(() => {
    // If ALL selected alerts are disabled, show "Enable" button
    // Otherwise show "Disable" button
    return alertsSelected.every((alert) => alert.disabled);
  }, [alertsSelected]);

  const updateAlertSelection = (rows: AlertsManagementTableData[]) => {
    setAlertsSelected(rows);
  };

  useEffect(() => {
    if (!data || workspace === undefined) return;
    setColumns(data.columns || []);

    const f = { ...data };
    f.workspaceId = workspace?.id;
    if (f.keyword === '') delete f.keyword;
    if (f.status === '') delete f.status;
    delete f.columns;
    setFilter(f);
  }, [data, workspace]);

  const filterWithColumns = useMemo(() => {
    return filter ? { ...filter, columns } : undefined;
  }, [filter, columns]);

  const columnsWithFilter = useColumns(filterWithColumns) as ColumnDef<
    any,
    any
  >[];

  const RenderHeader = () => {
    return (
      <>
        {showStatusButton &&
          alertsStatusButton &&
          (() => {
            const alertIds = alertsSelected.map((alert) => alert.id);
            return alertsStatusButton({
              alertIds,
              isEnableAction,
              onStatusChange: () => setAlertsSelected([]),
            });
          })()}

        {showAlertMenu &&
          alertsSelected &&
          alertsSelected.length === 1 &&
          (() => {
            const selectedId = alertsSelected[0].id;
            return (
              <EditAlertMenu
                alertId={selectedId}
                label={t('alertsManagement.menu.editAlert')}
              />
            );
          })()}
      </>
    );
  };

  return !filter ? null : (
    <InfiniteDataGrid
      modelName="workflow"
      query={LIST_ALERTS_MANAGEMENT}
      orderBy={orderData}
      filter={filter}
      pageSize={50}
      columns={columnsWithFilter}
      listMaxHeight={maxHeight as number}
      paddingBottom={paddingBottom as number}
      textEmpty={t('alertsManagement.common.textEmpty')}
      dataPrefix="workflow.listAlerts"
      enableScrollRefresh={true}
      preserveScrollKey={preserveScrollKey}
      showScrollIndicator={true}
      exportFileName="alerts"
      updateRowsSelected={(rows) => updateAlertSelection(rows)}
      headerContent={RenderHeader()}
      onOrderByChange={setOrderData}
      onRowClick={(r) => onRowClick(r)}
    />
  );
};
